/**
 * Toasts that stay until the user handles them, and that a component keeps
 * in sync with its own state: the version / session / idle notices and the
 * Compli job-progress toasts.
 *
 * Sonner calls a toast's `onDismiss` for a programmatic `toast.dismiss(id)` as
 * well as for the user's × / swipe, so a component that hides its toast because
 * its own state changed would otherwise be told "the user dismissed it".
 * `hidePersistentToast` marks the hide as ours, and `showPersistentToast`
 * swallows the resulting `onDismiss`. Only a real user dismissal reaches the
 * caller's handler.
 *
 * A hide is marked only while the toast is on screen, and a re-show clears any
 * mark, so a duplicate hide or a hide after the toast already went (an unmount
 * racing the user's ×) cannot leave a stale mark that would swallow a later
 * real dismissal of the same id.
 *
 * Each show is a new generation of its id, and a toast's callbacks act only
 * while theirs is the latest. A hide followed quickly by a re-show (an owner
 * remounting, or Strict Mode replaying its effects) can leave the old toast's
 * exit `onDismiss` arriving after the new one is up; it is then ignored, so it
 * neither reads as the user closing the new toast nor clears its tracking.
 */

import { notifyToast, toast, type ToastOptions, type ToastTelemetry, type TypedToastVariant } from './toast'

type ToastId = string | number

/** Ids raised by `showPersistentToast` whose toast has not closed yet. */
const onScreen = new Set<ToastId>()

/** Ids we hid ourselves and whose `onDismiss` has not fired yet. */
const hiddenByUs = new Set<ToastId>()

/** The latest show of each id still tracked; older callbacks are stale. */
const generationOf = new Map<ToastId, number>()
let generations = 0

export type PersistentToastOptions = Omit<ToastOptions, 'id' | 'duration'> & {
  id: ToastId
  /** Defaults to Infinity. Pass a number to let it expire (a finished job). */
  duration?: number
}

/**
 * Raise, or update in place, a toast identified by `options.id`. `onDismiss`
 * (and `onAutoClose`) fire only for the user closing it or it expiring — never
 * for `hidePersistentToast`. `telemetry` reports it under a named surface.
 */
export function showPersistentToast(
  variant: TypedToastVariant,
  title: React.ReactNode,
  options: PersistentToastOptions,
  telemetry?: ToastTelemetry,
): ToastId {
  const { id } = options
  generations += 1
  const generation = generations
  generationOf.set(id, generation)
  hiddenByUs.delete(id)
  onScreen.add(id)
  // A callback from an earlier show of this id, firing late, is not about the
  // toast now on screen.
  const isCurrent = () => generationOf.get(id) === generation
  const closed = () => {
    onScreen.delete(id)
    generationOf.delete(id)
  }
  const opts: ToastOptions = {
    ...options,
    duration: options.duration ?? Infinity,
    onDismiss: (t) => {
      if (!isCurrent()) return
      closed()
      if (hiddenByUs.delete(id)) return
      options.onDismiss?.(t)
    },
    onAutoClose: (t) => {
      if (!isCurrent()) return
      closed()
      hiddenByUs.delete(id)
      options.onAutoClose?.(t)
    },
  }
  return telemetry ? notifyToast(variant, title, opts, telemetry) : toast[variant](title, opts)
}

/** Remove a persistent toast because the owning state says it should be gone. */
export function hidePersistentToast(id: ToastId): void {
  // Mark the hide only if the toast is still up: an absent toast fires no
  // `onDismiss` to consume the mark, which would then swallow the next one.
  if (onScreen.delete(id)) hiddenByUs.add(id)
  toast.dismiss(id)
}
